import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import toast from 'react-hot-toast';

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
  avatarUrl?: string;
  tenantId?: string;
}

interface Tenant {
  id: string;
  name: string;
  // Add other tenant fields if known
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, senha: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message: string }>;
  resetPassword: (token: string, newPassword: string, confirmPassword: string) => Promise<{ success: boolean; message: string }>;
  setUser: (user: User | null) => void;
  setToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = '/api';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Interceptar requisições para adicionar token
  const apiRequest = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });
  }, [token]);

  const login = async (email: string, senha: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, senha }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || 'Erro ao fazer login');
        return false;
      }

      const { token: newToken, user: userData } = data.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('auth_token', newToken);

      toast.success('Login realizado com sucesso!');

      // Auto-select tenant if not set
      // Auto-select tenant if not set or if invalid
      try {
        // Fetch tenants for this user
        const tenantsResponse = await fetch(`${API_BASE_URL}/user-tenants`, {
          headers: { 'Authorization': `Bearer ${newToken}` }
        });

        if (tenantsResponse.ok) {
          const tenantsData = await tenantsResponse.json();
          const tenants = tenantsData.data || [];

          if (tenants.length > 0) {
            const currentTenant = localStorage.getItem('selected_tenant_id');
            const validTenant = tenants.find((t: Tenant) => t.id === currentTenant);

            // If no tenant selected, OR selected tenant is not in user's list
            // Force selection of the first available tenant
            const isSuperAdmin = userData.role === 'SUPERADMIN';
            
            if (!currentTenant || (!validTenant && !isSuperAdmin)) {
              const defaultTenantId = tenants[0].id;
              localStorage.setItem('selected_tenant_id', defaultTenantId);
              console.log('AuthContext: Auto-selected default tenant:', defaultTenantId);

              // If the user object didn't have a tenantId (SuperAdmin case), update it locally context-wise
              // (Note: This doesn't change the token, but helps UI context if used)
              if (!userData.tenantId) {
                setUser({ ...userData, tenantId: defaultTenantId });
              }
            } else if (currentTenant && isSuperAdmin && !validTenant) {
              // For SuperAdmin, if we have a currentTenant but it's not in the user-tenants list,
              // it might be a valid tenant they accessed previously. 
              // We keep it, but ensure the user object has it for UI consistency.
              if (!userData.tenantId || userData.tenantId !== currentTenant) {
                setUser({ ...userData, tenantId: currentTenant });
              }
              console.log('AuthContext: Keeping existing tenant for SuperAdmin:', currentTenant);
            }
          } else {
            // No tenants found - user may be in process of setup
          }
        }
      } catch (e) {
        console.error('Error auto-selecting tenant', e);
      }

      return true;
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error('Erro de conexão. Tente novamente.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    toast.success('Logout realizado com sucesso!');
  }, []);

  const forgotPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      return { success: response.ok, message: data.message || 'E-mail enviado com sucesso.' };
    } catch {
      return { success: false, message: 'Erro de conexão. Tente novamente.' };
    }
  };

  const resetPassword = async (
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword, confirmPassword }),
      });

      const data = await response.json();
      return { success: response.ok, message: data.message || 'Senha redefinida com sucesso.' };
    } catch {
      return { success: false, message: 'Erro de conexão. Tente novamente.' };
    }
  };

  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (!token) {
      setIsLoading(false);
      return false;
    }

    try {
      const response = await apiRequest('/auth/verify');

      if (!response.ok) {
        logout();
        return false;
      }

      const data = await response.json();
      const userData = data.data.user;
      
      // Sync tenantId for SuperAdmin if we have it in localStorage
      let currentTenantId = localStorage.getItem('selected_tenant_id');
      
      if (userData.role === 'SUPERADMIN') {
        if (currentTenantId) {
          userData.tenantId = currentTenantId;
        }
      } else if (userData.tenantId) {
        // For regular users, force sync with their assigned tenantId
        if (currentTenantId !== userData.tenantId) {
          localStorage.setItem('selected_tenant_id', userData.tenantId);
          currentTenantId = userData.tenantId;
        }
      }
      
      // If we still don't have a tenantId in localStorage but we have one in userData
      if (!currentTenantId && userData.tenantId) {
        localStorage.setItem('selected_tenant_id', userData.tenantId);
      }
      
      setUser(userData);
      return true;
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      logout();
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, apiRequest]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Interceptar respostas 401 da API para fazer logout automático
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Só faz logout se for uma resposta 401 de uma rota da API
      if (response.status === 401 && isAuthenticated && args[0]?.toString().includes('/api/')) {
        console.log('Token expirado ou inválido, fazendo logout...');
        logout();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, logout]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
    forgotPassword,
    resetPassword,
    setUser,
    setToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/* eslint-disable-next-line react-refresh/only-export-components */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};